import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../../components/ui/Dialog';
import { Button } from '../../../components/ui/Button';
import { FormField, Input } from '../../../components/ui/FormField';
import { useToast } from '../../../components/ui/Toast';
import { useUpdateLead } from '../hooks';
import type { LeadItem, UpdateLeadDto } from '../types';

interface EditLeadModalProps {
  lead: LeadItem | null;
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

export function EditLeadModal({ lead, open, onClose, onSuccess }: EditLeadModalProps) {
  const { toast } = useToast();
  const updateMutation = useUpdateLead();

  const [name, setName] = useState('');
  const [mobile, setMobile] = useState('');
  const [email, setEmail] = useState('');
  const [gender, setGender] = useState<'M' | 'F' | 'OTHER' | ''>('');
  const [source, setSource] = useState('WALK_IN');
  const [addressLine1, setAddressLine1] = useState('');
  const [addressLine2, setAddressLine2] = useState('');
  const [city, setCity] = useState('');
  const [stateCode, setStateCode] = useState('KA');
  const [pincode, setPincode] = useState('');
  const [notes, setNotes] = useState('');

  useEffect(() => {
    if (lead) {
      setName(lead.partyName || '');
      setMobile(lead.partyMobile || '');
      setEmail('');
      setGender('');
      setSource(lead.source || 'WALK_IN');
      if (lead.primaryAddress) {
        setAddressLine1(lead.primaryAddress.addressLine1 || '');
        setAddressLine2(lead.primaryAddress.addressLine2 || '');
        setCity(lead.primaryAddress.city || '');
        setStateCode(lead.primaryAddress.stateCode || 'KA');
        setPincode(lead.primaryAddress.pincode || '');
      } else {
        setAddressLine1('');
        setAddressLine2('');
        setCity('');
        setStateCode('KA');
        setPincode('');
      }
      setNotes('');
    }
  }, [lead]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!lead) return;

    if (!name.trim()) {
      toast({ type: 'error', title: 'Validation Error', description: 'Name is required' });
      return;
    }

    const payload: UpdateLeadDto = {
      source,
      prospect: {
        name: name.trim(),
        mobile: mobile.trim() || undefined,
        email: email.trim() || null,
        gender: (gender === 'M' || gender === 'F' || gender === 'OTHER') ? gender : undefined,
      },
    };

    if (addressLine1.trim() || city.trim() || pincode.trim()) {
      payload.address = {
        addressLine1: addressLine1.trim() || undefined,
        addressLine2: addressLine2.trim() || null,
        city: city.trim() || undefined,
        stateCode: stateCode.trim() || undefined,
        pincode: pincode.trim() || undefined,
      };
    }

    if (notes.trim()) {
      payload.notes = notes.trim();
    }

    try {
      await updateMutation.mutateAsync({ id: lead.id, body: payload });
      toast({ type: 'success', title: 'Lead Updated', description: 'Lead details updated successfully.' });
      onClose();
      onSuccess?.();
    } catch (err: unknown) {
      console.error('Failed to update lead:', err);
      const message = err instanceof Error ? err.message : 'Failed to update lead';
      toast({ type: 'error', title: 'Error', description: message });
    }
  };

  return (
    <Dialog open={open} onOpenChange={(val) => !val && onClose()}>
      <DialogContent className="sm:max-w-xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Edit Lead Details</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 py-2">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <FormField label="Prospect Name" required>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Full Name"
                required
              />
            </FormField>

            <FormField label="Mobile Number">
              <Input
                value={mobile}
                onChange={(e) => setMobile(e.target.value)}
                placeholder="10-digit mobile number"
              />
            </FormField>

            <FormField label="Email Address">
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="email@example.com"
              />
            </FormField>

            <FormField label="Lead Source" required>
              <select
                value={source}
                onChange={(e) => setSource(e.target.value)}
                className="w-full h-10 px-3 bg-surface-1 border border-border rounded-md text-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-accent-500"
              >
                <option value="WALK_IN">Walk-in</option>
                <option value="REFERRAL">Customer Referral</option>
                <option value="AGENT">Field Agent</option>
                <option value="CAMPAIGN">Marketing Campaign</option>
                <option value="WEBSITE">Digital / Website</option>
                <option value="OTHER">Other</option>
              </select>
            </FormField>
          </div>

          <div className="border-t border-border pt-3">
            <h4 className="text-xs font-semibold text-text-secondary uppercase tracking-wider mb-2">
              Primary Address (Optional)
            </h4>
            <div className="space-y-3">
              <FormField label="Address Line 1">
                <Input
                  value={addressLine1}
                  onChange={(e) => setAddressLine1(e.target.value)}
                  placeholder="Street / Building / Door No"
                />
              </FormField>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <FormField label="City">
                  <Input
                    value={city}
                    onChange={(e) => setCity(e.target.value)}
                    placeholder="City / Town"
                  />
                </FormField>

                <FormField label="State Code">
                  <Input
                    value={stateCode}
                    onChange={(e) => setStateCode(e.target.value.toUpperCase())}
                    placeholder="e.g. KA, TN, MH"
                    maxLength={2}
                  />
                </FormField>

                <FormField label="PIN Code">
                  <Input
                    value={pincode}
                    onChange={(e) => setPincode(e.target.value)}
                    placeholder="6-digit PIN"
                    maxLength={6}
                  />
                </FormField>
              </div>
            </div>
          </div>

          <div className="border-t border-border pt-3">
            <FormField label="Update Notes / Follow-up">
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Add latest remarks, customer preference, or next steps..."
                className="w-full h-20 p-2.5 text-sm bg-surface-1 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-accent-500 text-text-primary"
              />
            </FormField>
          </div>

          <DialogFooter className="pt-2 border-t border-border">
            <Button variant="secondary" type="button" onClick={onClose} disabled={updateMutation.isPending}>
              Cancel
            </Button>
            <Button type="submit" loading={updateMutation.isPending}>
              Save Changes
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
